import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Send, Loader2, MessageSquare } from 'lucide-react';

/**
 * In-app reply thread for an action ticket. Either the ticket owner or the
 * assigned partner can post; the other party is notified by email.
 *
 * Usage:
 *   <TicketThreadDrawer ticketId={id} open={open} onClose={...} clientName={...} />
 */
export default function TicketThreadDrawer({ ticketId, open, onClose, clientName, viewerRole = 'partner' }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchThread = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const r = await api.getTicketThread(ticketId);
      setTicket(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load thread');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setReplyBody('');
      fetchThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [ticket?.notes?.length]);

  const sendReply = async () => {
    if (!replyBody.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.replyToTicket(ticketId, replyBody.trim());
      setTicket(r.data);
      setReplyBody('');
      toast.success('Reply sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send reply');
    } finally {
      setSending(false);
    }
  };

  const notes = ticket?.notes || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-2xl flex flex-col p-0 overflow-hidden" data-testid="ticket-thread-drawer">
        <DialogHeader className="px-5 py-4 border-b border-[#1A1A1A]">
          <DialogTitle className="font-serif text-white text-base flex items-center gap-2">
            <MessageSquare size={14} className="text-gold" />
            {ticket?.title || 'Conversation'}
          </DialogTitle>
          {ticket && (
            <p className="text-[11px] text-muted-foreground mt-1">
              {clientName ? <span className="text-zinc-300">{clientName}</span> : null}
              {clientName && ticket.asset_name && <span className="mx-1 text-zinc-600">·</span>}
              {ticket.asset_name && <span>{ticket.asset_name}</span>}
              <span className="mx-1 text-zinc-600">·</span>
              <span className="capitalize">{ticket.issue}</span>
            </p>
          )}
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-[280px] max-h-[55vh] bg-[#0A0A0A]" data-testid="ticket-thread-messages">
          {loading && (
            <div className="text-center py-8 text-xs text-muted-foreground">
              <Loader2 size={16} className="animate-spin mx-auto mb-2 text-gold" /> Loading...
            </div>
          )}
          {!loading && ticket?.detail && (
            <div className="bg-[#080808] border border-[#1F1F1F] rounded p-3" data-testid="thread-initial-detail">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Original request</p>
              <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{ticket.detail}</p>
            </div>
          )}
          {!loading && notes.length === 0 && !ticket?.detail && (
            <p className="text-center text-xs text-muted-foreground py-8">No messages yet. Be the first to reply.</p>
          )}
          {!loading && notes.map((n, i) => {
            const isMe = (n.author_role || (n.author_id ? null : 'owner')) === viewerRole;
            const role = n.author_role || 'owner';
            return (
              <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`thread-note-${i}`}>
                <div className={`max-w-[80%] rounded p-3 ${isMe ? 'bg-gold/10 border border-gold/30' : 'bg-[#0F0F0F] border border-[#262626]'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    <span className={role === 'partner' ? 'text-gold' : 'text-zinc-300'}>{n.author_name || (role === 'partner' ? 'Advisor' : 'Client')}</span>
                    {n.at && <span className="ml-2 text-zinc-500 normal-case tracking-normal">{new Date(n.at).toLocaleString()}</span>}
                  </p>
                  <p className="text-xs text-white whitespace-pre-wrap leading-relaxed">{n.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-[#1A1A1A] p-4 space-y-2">
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            rows={3}
            placeholder={viewerRole === 'partner' ? 'Write a reply to your client...' : 'Reply to your advisor...'}
            disabled={loading}
            className="w-full bg-[#080808] border border-[#262626] rounded p-3 text-sm text-white leading-relaxed focus:outline-none focus:border-gold/40 disabled:opacity-50"
            data-testid="thread-reply-input"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground">
              {viewerRole === 'partner' ? 'Your client will be emailed when you reply.' : 'Your advisor will be emailed when you reply.'}
            </p>
            <Button
              onClick={sendReply}
              disabled={sending || !replyBody.trim()}
              className="bg-gold text-black hover:bg-gold/90 px-4"
              data-testid="thread-send-reply"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={12} className="mr-1.5" /> Send</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
